import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { IconApple } from 'components/Icon/IconApple'
import { IconBinoculars } from 'components/Icon/IconBinoculars'
import { IconCard } from 'components/Icon/IconCard'
import { IconCheckShield } from 'components/Icon/IconCheckShield'
import { IconCog } from 'components/Icon/IconCog'
import { IconDoctor } from 'components/Icon/IconDoctor'
import { IconDollarCircle } from 'components/Icon/IconDollarCircle'
import { IconDownload } from 'components/Icon/IconDownload'
import { IconDrugs } from 'components/Icon/IconDrugs'
import { IconEllipsis } from 'components/Icon/IconEllipsis'
import { IconGrid } from 'components/Icon/IconGrid'
import { IconInvoice } from 'components/Icon/IconInvoice'
import { IconLaptop } from 'components/Icon/IconLaptop'
import { IconPeople } from 'components/Icon/IconPeople'
import { IconPharmacy } from 'components/Icon/IconPharmacy'
import { IconPieChart } from 'components/Icon/IconPieChart'
import { IconSearchDollar } from 'components/Icon/IconSearchDollar'
import { IconShoppingBag } from 'components/Icon/IconShoppingBag'
import getSubdomain from 'utils/urls/getSubdomain'
import { ActionCard } from '../ActionCard/ActionCard'
import { IconClipboardUser } from 'components/Icon/IconClipboardUser'
import { useEvent } from 'events/useEvent'
import { MyLinkClickHandler } from 'components/MyLink'

const medicareEnv = process.env.REACT_APP_MEDICARE_ENV || ''
const mymedicareEnv = process.env.REACT_APP_MY_MEDICARE_ENV || ''

const medicareSubdomain = getSubdomain(medicareEnv)
const esMedicareSubdomain = getSubdomain(medicareEnv, 'www')
const mymedicareSubdomain = getSubdomain(mymedicareEnv)

export const AccountHomeTaskMap = {
  allOptions: {
    i18nKey: 'allOptions',
    href: '/home/allOptions',
    // TODO: Validate
    elementIdSuffix: 'alloptions_click',
    icon: <IconEllipsis width="28px" height="28px" />
  },
  checkProviders: {
    i18nKey: 'providers.checkProviders',
    href: {
      en: `https://${medicareSubdomain}medicare.gov/care-compare/my-providers`,
      es: `https://es.${esMedicareSubdomain}medicare.gov/care-compare/my-providers`
    },
    elementIdSuffix: 'checkmyproviders_click',
    icon: <IconDoctor width="28px" height="28px" />
  },
  findProviders: {
    i18nKey: 'providers.findProviders',
    href: {
      en: `https://${medicareSubdomain}medicare.gov/care-compare`,
      es: `https://es.${esMedicareSubdomain}medicare.gov/care-compare`
    },
    elementIdSuffix: 'findproviders_click',
    icon: <IconBinoculars width="28px" height="28px" />
  },
  checkPreventiveServices: {
    i18nKey: 'providers.checkPreventiveServices',
    href: '/preventive-services',
    elementIdSuffix: 'checkmypreventiveservices_click',
    icon: <IconApple width="28px" height="28px" />
  },
  updatePharmacies: {
    i18nKey: 'providers.updatePharmacies',
    href: {
      en: 'https://www.medicare.gov/plan-compare/#/manage-pharmacies?lang=en&year=2023',
      es: 'https://www.medicare.gov/plan-compare/#/manage-pharmacies?lang=es&year=2023'
    },
    elementIdSuffix: 'updatemysavedpharmacies_click',
    icon: <IconPharmacy width="28px" height="28px" />
  },
  updateDrugs: {
    i18nKey: 'providers.updateDrugs',
    href: {
      en: 'https://www.medicare.gov/plan-compare/#/view-prescriptions?fips=&lang=en&year=2023',
      es: 'https://www.medicare.gov/plan-compare/#/view-prescriptions?fips=&lang=es&year=2023'
    },
    elementIdSuffix: 'updatemysaveddrugs_click',
    icon: <IconDrugs width="28px" height="28px" />
  },
  checkClaims: {
    i18nKey: 'claims.checkClaims',
    href: '/claims',
    elementIdSuffix: 'checkmyclaims_click',
    icon: <IconInvoice width="28px" height="28px" />
  },
  getEmsns: {
    i18nKey: 'claims.getEmsns',
    href: '/eMSN',
    elementIdSuffix: 'getmyemsn_click',
    icon: <IconLaptop width="28px" height="28px" />
  },
  getEmsns2: {
    i18nKey: 'claims.getEmsns2',
    href: '/account-settings',
    elementIdSuffix: 'getmyemsn_click',
    icon: <IconLaptop width="28px" height="28px" />
  },
  checkDeductible: {
    i18nKey: 'claims.checkDeductible',
    href: '/claims/claim-deductibles',
    elementIdSuffix: 'checkmydeductible_click',
    icon: <IconPieChart width="28px" height="28px" />
  },
  planDetails: {
    i18nKey: 'plans.planDetails',
    href: {
      en: `https://${mymedicareSubdomain}medicare.gov/mbp/plansandcoverage.aspx`,
      es: `https://${mymedicareSubdomain}medicare.gov/mbp/plansandcoverage.aspx?Lang=es-mx`
    },
    elementIdSuffix: 'getplandetails_click',
    icon: <IconCheckShield width="28px" height="28px" />
  },
  findPlans: {
    i18nKey: 'plans.findPlans',
    href: {
      en: `https://${medicareSubdomain}medicare.gov/plan-compare/?year=2023`,
      es: `https://${medicareSubdomain}medicare.gov/plan-compare/#/?year=2023&lang=es`
    },
    elementIdSuffix: 'findhealthanddrugplans_click',
    icon: <IconShoppingBag width="28px" height="28px" />
  },
  checkPremiums: {
    i18nKey: 'premiums.checkPremiums',
    href: {
      en: `https://${mymedicareSubdomain}medicare.gov/mbp/PremiumInformation.aspx`,
      es: `https://${mymedicareSubdomain}medicare.gov/mbp/PremiumInformation.aspx?Lang=es-mx`
    },
    elementIdSuffix: 'checkmypremiums_click',
    icon: <IconSearchDollar width="28px" height="28px" />
  },
  payPremium: {
    i18nKey: 'premiums.payPremium',
    href: {
      en: `https://${mymedicareSubdomain}medicare.gov/mbp/premiumpayment.aspx`,
      es: `https://${mymedicareSubdomain}medicare.gov/mbp/premiumpayment.aspx?Lang=es-mx`
    },
    elementIdSuffix: 'paymypremiums_click',
    icon: <IconDollarCircle width="28px" height="28px" />
  },
  editAccountSettings: {
    i18nKey: 'settings.editAccountSettings',
    href: '/account-settings',
    elementIdSuffix: 'editmyaccountsettings_click',
    icon: <IconCog width="28px" height="28px" />
  },
  manageReps: {
    i18nKey: 'settings.manageReps',
    href: '/account-settings/auth-rep',
    elementIdSuffix: 'managemyreps_click',
    icon: <IconPeople width="28px" height="28px" />
  },
  getMedicareCard: {
    i18nKey: 'settings.getMedicareCard',
    href: '/account-settings/get-my-medicare-card',
    elementIdSuffix: 'getmymedicarecard_click',
    icon: <IconCard width="28px" height="28px" />
  },
  download: {
    i18nKey: 'settings.download',
    href: {
      en: `https://${mymedicareSubdomain}medicare.gov/mbp/DownloadMyData.aspx`,
      es: `https://${mymedicareSubdomain}medicare.gov/mbp/DownloadMyData.aspx?Lang=es-mx`
    },
    elementIdSuffix: 'downloadmydata_click',
    icon: <IconDownload width="28px" height="28px" />
  },
  connectedApps: {
    i18nKey: 'settings.connectedApps',
    href: {
      en: `https://${mymedicareSubdomain}medicare.gov/mbp/connectedapps.aspx`,
      es: `https://${mymedicareSubdomain}medicare.gov/mbp/connectedapps.aspx?Lang=es-mx`
    },
    elementIdSuffix: 'openmyconnectedapps_click',
    icon: <IconGrid width="28px" height="28px" />
  },
  updateHealthInfo: {
    i18nKey: 'settings.updateHealthInfo',
    href: {
      en: `https://${mymedicareSubdomain}medicare.gov/mbp/myhealth.aspx`,
      es: `https://${mymedicareSubdomain}medicare.gov/mbp/myhealth.aspx?Lang=es-mx`
    },
    elementIdSuffix: 'updatemyhealthinfo_click',
    icon: <IconClipboardUser width="28px" height="28px" />
  }
}

export type AccountHomeTaskMapKey = keyof typeof AccountHomeTaskMap

type AccountHomeLinkProps = {
  className?: string
  item: AccountHomeTaskMapKey
  isLoading?: boolean
  isInModal?: boolean
}

export const AccountHomeLink = ({
  className,
  item,
  isLoading,
  isInModal
}: AccountHomeLinkProps) => {
  const { href, icon, i18nKey, elementIdSuffix } = AccountHomeTaskMap[item]
  const { t, i18n } = useTranslation('accountHome', {
    keyPrefix: 'actionLinks'
  })

  const link = useMemo(() => {
    return typeof href === 'string' ? href : href[i18n.language as 'en' | 'es']
  }, [href, i18n.language])

  // This is a potential problem area if our keys don't match up... not sure how to make this nicer besides
  // making a full duplicate of the task map. I suppose this is possible to create a generator. May do in a future PR
  // or in this one if I have enough time.
  const elementId = isInModal
    ? `accounthomemodal_${elementIdSuffix}`
    : `accounthome_${elementIdSuffix}`

  const { fireEvent } = useEvent('accountHome')
  const doFireEvent: MyLinkClickHandler = useCallback(
    ({ linkText, linkUrl }: { linkText?: string; linkUrl?: string }) => {
      // const isExternal = Boolean(/^https?:\/\//.test(link))
      const eventProps = {
        elementId,
        linkText,
        linkUrl
      }
      if (isInModal) {
        fireEvent('genericAccountHomeModalLink', eventProps)
      } else {
        fireEvent('genericAccountHomeLink', eventProps)
      }
    },
    [elementId, isInModal, fireEvent]
  )

  return (
    <ActionCard
      isLoading={isLoading}
      text={t(i18nKey)}
      icon={icon}
      href={link}
      handleClick={doFireEvent}
      className={`my-u-width--full ${className}`}
    />
  )
}
